// See documentation here https://www.notion.so/sera-systems/Pagination-Class-Usage-4c5352b67b3a4ad08e1cd91f551b1e67

import API from "serviceshift-ui/api-client";
import { reactive } from "vue";
export interface PaginationFilter {
  key: string;
  value: string | number | null;
}

export interface PaginationSort {
  key: string;
  direction: "asc" | "desc" | null;
}

export interface PaginationMeta {
  pagination: {
    page: number;
    from?: number;
    to?: number;
    per_page: number;
    total_pages: number;
    total_objects: number;
    links: {
      first: string;
      last: string;
      next?: string;
      prev?: string;
    };
  };
}

export interface PaginationRequestConfig {
  url: string;
  method: string;
  headers?: { [k: string]: any };
}

export default class Pagination {
  baseURL: string;
  page: number;
  perPage: number;
  filteredBy: PaginationFilter[];
  sortedBy: PaginationSort[];
  meta: PaginationMeta;
  headers: { [k: string]: any };

  constructor(args) {
    this.baseURL = args.baseURL;
    this.page = args.page || 1;
    this.perPage = args.perPage || 25;
    this.filteredBy = args.filteredBy || [];
    this.sortedBy = args.sortedBy || [];
    this.meta = reactive(
      args.meta ?? {
        pagination: {
          page: this.page,
          per_page: this.perPage,
          total_pages: 0,
          total_objects: 0,
          from: 0,
          to: 0,
          links: { first: "", last: "", next: undefined, prev: undefined }
        }
      }
    );
    this.headers = args.headers;

    this.first = this.first.bind(this);
    this.last = this.last.bind(this);
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
    this.goToPage = this.goToPage.bind(this);
    this.search = this.search.bind(this);
  }

  async getData(requestConfig?: PaginationRequestConfig) {
    return await this.makeRequest(
      requestConfig || this.buildRequestConfig(this.getPageParams(""))
    );
  }

  async first() {
    if (!this.meta?.pagination.links.first) return;
    return await this.changePage(this.meta?.pagination.links.first);
  }

  async next() {
    if (!this.meta?.pagination?.links?.next) return;
    return await this.changePage(this.meta?.pagination.links.next);
  }

  async prev() {
    if (!this.meta?.pagination?.links?.prev) return;
    return await this.changePage(this.meta?.pagination.links.prev);
  }

  async last() {
    if (!this.meta?.pagination?.links?.last) return;
    return await this.changePage(this.meta?.pagination.links.last);
  }

  async goToPage(page: number | string) {
    const requestConfig = this.buildRequestConfig({
      page: page.toString(),
      per_page: this.meta.pagination.per_page.toString()
    });
    return await this.getData(requestConfig);
  }

  async search(arg: string | number) {
    this.filter({ key: "search", value: arg || null });
    return await this.getData();
  }

  async setLimit(arg: string | number) {
    const requestConfig = this.buildRequestConfig({
      page: "1",
      per_page: arg.toString()
    });
    return await this.getData(requestConfig);
  }

  sort(arg: PaginationSort) {
    const index = this.sortedBy.findIndex((record) => record.key === arg.key);
    if (index > -1) {
      if (arg.direction !== null) {
        this.sortedBy[index] = arg;
      } else {
        this.sortedBy = this.sortedBy.filter((_, i) => i !== index);
      }
    } else {
      this.sortedBy = this.sortedBy.concat([arg]);
    }
  }

  filter(arg: PaginationFilter) {
    const index = this.filteredBy.findIndex((record) => record.key === arg.key);
    if (index > -1) {
      if (arg.value !== null) {
        this.filteredBy[index] = arg;
      } else {
        this.filteredBy = this.filteredBy.filter((_, i) => i !== index);
      }
    } else if (arg.value !== null) {
      this.filteredBy = this.filteredBy.concat([arg]);
    }
  }

  private async changePage(link: string) {
    const requestConfig = this.buildRequestConfig(this.getPageParams(link));
    return await this.getData(requestConfig);
  }

  private buildRequestConfig(pageParams?: {
    page?: string;
    per_page?: string;
  }) {
    const sort = this.buildSortParam();
    const filter = this.buildFilterParam();
    const queryString = new URLSearchParams({
      ...sort,
      ...filter,
      ...pageParams
    });
    const config: PaginationRequestConfig = {
      url: `${this.baseURL}?${queryString.toString()}`,
      method: "GET"
    };
    if (this.headers) {
      config.headers = this.headers;
    }
    return config;
  }

  private buildSortParam(): { sort?: string } {
    // https://jsonapi.org/format/#fetching-sorting
    if (!this.sortedBy.length) return {};
    const sortParam = this.sortedBy
      .map((_sort) => (_sort.direction === "desc" ? "-" : "") + _sort.key)
      .join(",");
    return { sort: sortParam };
  }

  private buildFilterParam(): {
    [k: string]: string;
  } {
    // https://jsonapi.org/format/#query-parameters-families
    if (!this.filteredBy.length) return {};
    const filterParam = this.filteredBy.reduce((filterParam, _filter) => {
      filterParam[`filter[${_filter.key}]`] = _filter.value?.toString();
      return filterParam;
    }, {});
    return filterParam;
  }

  private async makeRequest(requestConfig: PaginationRequestConfig) {
    const { url, ...rest } = requestConfig;
    const response = await API.makeRequest(url, { ...rest });
    if (response.data?.meta) Object.assign(this.meta, response.data.meta);
    return response;
  }
  private getPageParams(link: string): { page: string; per_page?: string } {
    const params = new URLSearchParams(
      link.substring(link.indexOf("?"), link.length)
    );
    const pageParams: { page: string; per_page?: string } = {
      page: params.get("page") || this.page.toString(),
      per_page:
        params.get("per_page") ||
        this.meta?.pagination?.per_page?.toString() ||
        this.perPage.toString()
    };
    return pageParams;
  }
}
